import React, { useState, useEffect, useMemo } from 'react';
import { FileSpreadsheet, Table, Library, BookOpen } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import { useNotifications } from '../../context/NotificationContext';
import { dsProjectApi } from '../../services/api';
import ProjectSheetsTab from './ProjectSheetsTab';
import ProjectCatalogTab from './ProjectCatalogTab';
import { IndexComposer, projectSource } from './IndexComposer';
import { errText } from './projectSheets';
import DefinitionsTab from './DefinitionsTab';
import DefinitionModal from './DefinitionModal';

// Project Datasheet Composer: this database's Index Sheet Datasheets and their values.
// The route is gated on utilities.ds.project.composer. The Catalog tab is an add-on that
// also needs utilities.ds.project.catalog, so it is hidden without that key.
const TABS = [
  { id: 'datasheets',  label: 'Index Sheet Datasheets', icon: FileSpreadsheet },
  { id: 'index',       label: 'Index Composer',         icon: Table },
  { id: 'catalog',     label: 'Catalog',                icon: Library, permKey: 'utilities.ds.project.catalog' },
  { id: 'definitions', label: 'Definitions',            icon: BookOpen },
];

const ProjectDatasheetComposerPage = () => {
  const { hasPermission } = useAuth();
  const { notify } = useNotifications();
  const dbCtx = useDatabaseSafe();
  const dbSlug = dbCtx?.dbSlug || '';
  const tabs = TABS.filter(t => !t.permKey || hasPermission(t.permKey, dbSlug));
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const [projectTemplates, setProjectTemplates] = useState([]);
  const [definitionLabel, setDefinitionLabel] = useState(null);   // label clicked in the Index Composer

  // The Index Composer works on this page's database only, so its database picker offers just it.
  const currentDb = dbCtx?.currentDb;
  const source = useMemo(() => ({
    ...projectSource,
    getDatabases: () => Promise.resolve({ data: currentDb ? [currentDb] : [] }),
  }), [currentDb]);

  // This project's templates feed the Index Composer's layout (form and type of each datasheet).
  // Re-read whenever the tab opens: a create or revise on the first tab adds templates.
  useEffect(() => {
    if (activeTab !== 'index') return;
    dsProjectApi.getTemplates()
      .then(r => setProjectTemplates(r.data ?? []))
      .catch(e => notify.error(errText(e, 'Failed to load this project\'s datasheets.')));
  }, [activeTab, notify]);

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <h1>Project Datasheet Composer</h1>
          <div className="stui-module-header-tabs">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button key={id} className={`stui-dataentry-tab${activeTab === id ? ' active' : ''}`}
                onClick={() => setActiveTab(id)}>
                <Icon size={14} /><span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      {activeTab === 'datasheets' && <ProjectSheetsTab />}
      {activeTab === 'index' && (
        <div className="stui-related-content">
          <IndexComposer allDsIndex={projectTemplates} source={source} onLabelClick={setDefinitionLabel} />
        </div>
      )}
      {activeTab === 'catalog' && <ProjectCatalogTab />}
      {activeTab === 'definitions' && <DefinitionsTab scope="project" />}
      {definitionLabel && <DefinitionModal label={definitionLabel} scope="project" onClose={() => setDefinitionLabel(null)} />}
    </div>
  );
};

export default ProjectDatasheetComposerPage;
